import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Plus, Gamepad2, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useServerMetrics } from '../hooks/useServerMetrics';
import { serverApi, packageApi } from '../services/api';
import { CreateServerModal } from '../components/dashboard/CreateServerModal';
import { ServerCard } from '../components/dashboard/ServerCard';
import { useTranslation } from 'react-i18next';
import type {
  AppliedServerSpec,
  OwnerServerOperation,
  ServerAutoShutdownState,
  ServerMonitoringSnapshot,
} from '../types/server';
import { getApiErrorDetails } from '../utils/apiError';

interface Server {
  _id: string;
  name: string;
  displayName: string;
  status: 'creating' | 'starting' | 'running' | 'stopping' | 'stopped' | 'error' | 'suspended';
  subdomain: string;
  connectionAddress: string | null;
  packageId?: {
    name: string;
    displayName: string;
    ram: number;
    cpu?: number;
    storage?: number;
    maxPlayers: number;
    price: number;
  } | null;
  appliedSpec?: AppliedServerSpec;
  gameSettings: {
    version: string;
    difficulty: string;
    mode: string;
  };
  monitoring: ServerMonitoringSnapshot;
  autoShutdown?: ServerAutoShutdownState;
  ownerOperation?: OwnerServerOperation | null;
  dnsRecords?: Array<{ type: string; name: string; content: string }>;
  createdAt: string;
}

export function DashboardPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { t } = useTranslation();

  // Subscribe to real-time server metrics updates via WebSocket
  const { connected: wsConnected } = useServerMetrics();

  const serversQuery = useQuery({
    queryKey: ['servers'],
    queryFn: serverApi.getUserServers,
    refetchInterval: 10_000,
  });

  const packagesQuery = useQuery({
    queryKey: ['packages'],
    queryFn: packageApi.getPackages,
  });

  const servers = serversQuery.data?.servers || [];
  const packages = packagesQuery.data?.packages || [];
  const serverError = serversQuery.isError
    ? getApiErrorDetails(serversQuery.error, t('dashboard.loadServersError')).message
    : null;
  const packageError = packagesQuery.isError
    ? getApiErrorDetails(packagesQuery.error, t('dashboard.loadPackagesError')).message
    : null;
  const canCreate = !packagesQuery.isPending && !packageError && packages.length > 0;

  if (serversQuery.isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-12 h-12" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-100">{t('dashboard.title')}</h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-gray-500 text-sm">{t('dashboard.greeting', { name: user?.name })}</p>
              <span className={`flex items-center gap-1 text-xs ${wsConnected ? 'text-green-500' : 'text-gray-500'}`}>
                {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {wsConnected ? t('common.live') : t('common.offline')}
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={!canCreate}
            className="btn-primary"
          >
            <Plus className="w-5 h-5" />
            {t('dashboard.newServer')}
          </button>
        </div>

        {serverError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-300">{t('dashboard.loadServersError')}</p>
              <p className="text-xs text-red-400/80 mt-1">{serverError}</p>
            </div>
            <button onClick={() => serversQuery.refetch()} className="btn-secondary text-sm">
              <RefreshCw className="w-4 h-4" />
              {t('dashboard.retry')}
            </button>
          </div>
        )}

        {packageError && (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-300">{t('dashboard.loadPackagesError')}</p>
              <p className="text-xs text-amber-400/80 mt-1">{packageError}</p>
            </div>
            <button onClick={() => packagesQuery.refetch()} className="btn-secondary text-sm">
              <RefreshCw className="w-4 h-4" />
              {t('dashboard.retry')}
            </button>
          </div>
        )}

        {!packagesQuery.isPending && !packageError && packages.length === 0 && (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm text-amber-300">
            {t('dashboard.noPackages')}
          </div>
        )}

        {/* Servers */}
        {!serverError && servers.length === 0 ? (
          <div className="empty-state">
            <Gamepad2 className="empty-state-icon" />
            <h3 className="empty-state-title">{t('dashboard.noServers')}</h3>
            <p className="empty-state-text">{t('dashboard.noServersDesc')}</p>
            <button
              onClick={() => setShowCreateModal(true)}
              disabled={!canCreate}
              className="btn-primary"
            >
              <Plus className="w-5 h-5" />
              {t('dashboard.createServer')}
            </button>
          </div>
        ) : !serverError ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servers.map((server: Server) => (
              <ServerCard
                key={server._id}
                server={server}
              />
            ))}
          </div>
        ) : null}

        {showCreateModal && (
          <CreateServerModal
            packages={packages}
            onClose={() => setShowCreateModal(false)}
            onSuccess={(serverId) => {
              setShowCreateModal(false);
              queryClient.invalidateQueries({ queryKey: ['servers'] });
              navigate(`/servers/${serverId}`);
            }}
          />
        )}
      </div>
    </div>
  );
}
